<script setup lang="ts">
import type { ContentNode } from "#layers/books/types";

const props = defineProps<{
  node: ContentNode;
  currentPath: string;
  expandedNodes: Set<string>;
  depth?: number;
}>();

const emit = defineEmits<{
  toggle: [nodeId: string];
}>();

const depth = computed(() => props.depth ?? 0);

const isExpanded = computed(() => props.expandedNodes.has(props.node.id));
const isActive = computed(() => {
  // Check if this node's path matches the current path
  const nodePath = props.node.path.replace(/^\/book\/[^/]+\/?/, "");
  return nodePath === props.currentPath;
});
const hasChildren = computed(
  () => props.node.children && props.node.children.length > 0,
);

function handleToggle(e: Event) {
  e.preventDefault();
  e.stopPropagation();
  emit("toggle", props.node.id);
}
</script>

<template>
  <div>
    <NuxtLink
      :to="node.path"
      class="flex items-center gap-2 py-1.5 px-2 rounded-md text-sm transition-colors"
      :class="[
        isActive
          ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 font-medium'
          : 'hover:bg-gray-100 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300',
      ]"
      :style="{ paddingLeft: `${depth * 16 + 8}px` }"
    >
      <!-- Expand/collapse button for containers -->
      <button
        v-if="hasChildren"
        type="button"
        class="p-0.5 hover:bg-gray-200 dark:hover:bg-gray-700 rounded"
        @click="handleToggle"
      >
        <UIcon
          :name="
            isExpanded
              ? 'i-heroicons-chevron-down'
              : 'i-heroicons-chevron-right'
          "
          class="w-4 h-4"
        />
      </button>
      <span v-else class="w-5" />

      <!-- Icon -->
      <UIcon
        :name="
          node.type === 'container'
            ? 'i-heroicons-folder'
            : 'i-heroicons-document-text'
        "
        class="w-4 h-4 flex-shrink-0"
        :class="node.type === 'container' ? 'text-amber-500' : 'text-gray-400'"
      />

      <!-- Title -->
      <span class="truncate">{{ node.title }}</span>
    </NuxtLink>

    <!-- Children -->
    <div v-if="hasChildren && isExpanded">
      <BookTreeNode
        v-for="child in node.children"
        :key="child.id"
        :node="child"
        :current-path="currentPath"
        :expanded-nodes="expandedNodes"
        :depth="depth + 1"
        @toggle="emit('toggle', $event)"
      />
    </div>
  </div>
</template>
